import React, { Fragment, memo, MouseEvent, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { ActionButton } from 'src/components/shared/ActionButton'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { MenuActionItem } from 'src/interfaces/ui'
import { useSelector } from 'react-redux'
import { colors } from 'src/theme'
import { CirclePlusMinor, PlusMinor } from '@shopify/polaris-icons'
import { getTags } from 'src/selectors/tag'
import { Tag } from 'src/interfaces/tag'
import { Modal } from '@mui/material'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { CreateTagForm } from './create-tag-form/CreateTagForm'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

const slugForTestId = (value: string) =>
  value
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'tag'

interface Props {
  selectedTags?: Array<Tag>
  onSelectTag(tag: Tag): void
  addTagTriggerDataTestId?: string
  /** When set, menu rows get `prefix-option-{slug}` and create-new uses `prefix-create-new-btn`. */
  tagMenuItemTestIdPrefix?: string
}

const AddTagButtonComponent = ({
  selectedTags,
  onSelectTag,
  addTagTriggerDataTestId,
  tagMenuItemTestIdPrefix,
}: Props) => {
  const tags = useSelector(getTags)
  const { hasPermission } = useCheckPermissions()
  const canEditTags = hasPermission(UserPermission.TagsEdit)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])
  const container = useMemo(() => document.getElementById('root'), [])

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const filteredTags = useMemo(
    () =>
      tags.filter(
        (item) =>
          !selectedTags || !selectedTags.some((selectedTag) => selectedTag.name === item.name),
      ),
    [tags, selectedTags],
  )

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    const prefix = tagMenuItemTestIdPrefix

    const items: Array<MenuActionItem> = filteredTags.map((item) => ({
      label: item.name,
      dataTestId: prefix ? `${prefix}-option-${slugForTestId(item.name)}` : undefined,
      onClick: () => {
        onSelectTag(item)
        setAnchorEl(null)
      },
    }))

    if (canEditTags) {
      items.push({
        label: 'Create new tag',
        className: filteredTags.length
          ? tw`border-t border-0 border-solid border-[#EDEDED]`
          : undefined,
        color: colors.PRIMARY_PURPLE_500,
        icon: <CirclePlusMinor width={20} fill={colors.PRIMARY_PURPLE_500} />,
        dataTestId: prefix ? `${prefix}-create-new-btn` : undefined,
        onClick: () => {
          handleOpenModal()
          setAnchorEl(null)
        },
      })
    }

    return items
  }, [filteredTags, onSelectTag, tagMenuItemTestIdPrefix, canEditTags])

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleCreateNewTag = useCallback(
    (tag: Tag) => {
      onSelectTag(tag)
      handleCloseModal()
    },
    [onSelectTag],
  )

  return (
    <Fragment>
      <ActionButton
        className={tw`px-2 h-[28px] rounded`}
        selected={!!anchorEl}
        color="light"
        text="Add tag"
        icon={<PlusMinor width={20} fill={colors.PRIMARY_PURPLE_500} />}
        onClick={handleClick}
        data-testid={addTagTriggerDataTestId}
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        itemClassName={tw`min-w-[180px]`}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleCloseMenu}
      />
      <Modal
        container={container}
        open={Boolean(isModalOpen)}
        onClose={handleCloseModal}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
        css={tw`flex justify-center items-center backdrop-blur`}
      >
        <div
          data-testid={
            tagMenuItemTestIdPrefix ? `${tagMenuItemTestIdPrefix}-create-tag-modal` : undefined
          }
          css={tw`bg-white rounded-lg`}
        >
          <ModalHeader
            title="Create new tag"
            onClose={handleCloseModal}
            closeButtonDataTestId={
              tagMenuItemTestIdPrefix
                ? `${tagMenuItemTestIdPrefix}-create-tag-modal-close-btn`
                : undefined
            }
          />
          <CreateTagForm onSubmit={handleCreateNewTag} onClose={handleCloseModal} />
        </div>
      </Modal>
    </Fragment>
  )
}

export const AddTagButton = memo(AddTagButtonComponent)
